(ns ui.captions.actions
  "The action handlers of the captions sidebar: transcribing the media
   of a clip via the `:command/captions-transcribe` command, storing
   the words as `:word/*` entities of the media asset, giving the clip
   its `:clip/captions`, and editing the words."
  (:require [app.captions.transcript-edit :as transcript-edit]
            [datascript.core :as ds]
            [parts.replicant.command :as command]
            [ui.captions.editor :as editor]
            [ui.captions.pricing :as pricing]
            [ui.captions.query :as query]
            [ui.captions.styles :as styles]
            [ui.editor2.actions :as actions]
            [ui.editor2.common :as common]))

(defn transcription-range
  "The section of the media to transcribe: the whole media, so every
   clip of it on every timeline finds its words, or only the clip's
   range when the media is longer than the command accepts (see
   `ui.captions.pricing/transcribed-ms`)."
  [clip]
  (let [duration (get-in clip [:clip/asset :asset/duration])]
    (if (and duration
             (<= duration pricing/max-transcription-ms))
      {:offset-ms 0
       :duration-ms duration}
      (query/clip-range clip))))

(defn transcribe-command
  [clip]
  {:command/kind :command/captions-transcribe
   :command/uuid (random-uuid)
   :command/data (assoc (transcription-range clip)
                        :source-uri (query/source-url clip))})

(def transcribe-again-question
  (str "Transcribing again replaces the transcript of this clip, "
       "including your corrections, emphasis and phrase breaks. Continue?"))

(defn transcribe-again?
  "Whether to go ahead: a clip whose asset already has a transcript
   asks first, since the transcription replaces every edit."
  [clip]
  (or (empty? (get-in clip [:clip/asset :asset/words]))
      (js/confirm transcribe-again-question)))

(defn transcribe!
  [{:keys [store]}]
  (let [state @store]
    (when-let [clip (query/source-clip state)]
      (when (and (query/source-url clip)
                 (transcribe-again? clip))
        (let [cmd (transcribe-command clip)]
        (swap! store
               update-in [:editor2 :captions-sidebar]
               assoc
               :command cmd
               :source-clip (:clip/uuid clip)
               :error nil)
        ((:replicant/dispatch! state)
         {:actions [[:data/command cmd
                     {:on-success [[:captions/transcribed]]
                      :on-error [[:captions/failed]]}]]}))))))

(defn word-entities
  [words]
  (mapv (fn [word]
          (assoc word :word/uuid (random-uuid)))
        words))

(defn transcript-tx
  "Replaces the transcript of the media asset."
  [media-asset words]
  (concat
    (map (fn [word]
           [:db/retractEntity (:db/id word)])
         (:asset/words media-asset))
    [{:asset/uuid (:asset/uuid media-asset)
      :asset/words (word-entities words)}]))

(defn captions-tx
  "Gives the clip captions in the style, keeping the other settings of
   captions it has."
  [clip style]
  [{:clip/uuid (:clip/uuid clip)
    :clip/captions (assoc (:clip/captions clip)
                          :captions/style style)}])

(defn add-captions
  "The clip gets captions in the default style and the sidebar sticks
   to it."
  [state clip]
  (-> state
      (common/transact (captions-tx clip styles/default-style))
      (assoc-in [:editor2 :captions-sidebar :source-clip] (:clip/uuid clip))))

(defn add-captions!
  "Adds captions to a clip whose asset already has a transcript."
  [{:keys [store]}]
  (let [clip (query/source-clip @store)]
    (when (and clip
               (not (query/captioned? clip)))
      (swap! store add-captions clip))))

(defn remove-captions!
  "Takes the captions off the clip. The transcript stays on the asset,
   so adding them again needs no transcription."
  [{:keys [store]}]
  (let [clip (query/source-clip @store)]
    (when (query/captioned? clip)
      (swap! store
             common/transact
             [[:db/retract [:clip/uuid (:clip/uuid clip)]
               :clip/captions
               (:clip/captions clip)]]))))

(defn no-speech
  "A clip without speech keeps the transcript it may have."
  [state]
  (update-in state
             [:editor2 :captions-sidebar]
             (fn [sidebar]
               (-> sidebar
                   (dissoc :command)
                   (assoc :error "No speech was found in this clip")))))

(defn transcribed!
  "Stores the words on the media asset and gives the clip captions
   when it has none yet."
  [{:keys [store]}]
  (let [state @store
        cmd (get-in state [:editor2 :captions-sidebar :command])
        words (:captions/words (command/get-latest-result state cmd))
        source (query/source-clip state)]
    (if (empty? words)
      (swap! store no-speech)
      (swap! store
             (fn [state]
               (-> state
                   (common/transact (transcript-tx (:clip/asset source) words))
                   (cond-> (not (query/captioned? source)) (add-captions source))
                   (update-in [:editor2 :captions-sidebar] dissoc :command)))))))

(defn failed!
  [{:keys [store]}]
  (let [state @store
        cmd (get-in state [:editor2 :captions-sidebar :command])
        error (or (command/get-latest-error state cmd)
                  "Transcription failed")]
    (swap! store
           update-in [:editor2 :captions-sidebar]
           (fn [sidebar]
             (-> sidebar
                 (dissoc :command)
                 (assoc :error error))))))

(defn set-source!
  [{:keys [store action]}]
  (let [[_ clip-uuid-str] action]
    (swap! store
           assoc-in [:editor2 :captions-sidebar :source-clip]
           (uuid clip-uuid-str))))

(defn set-style!
  "Picks the style and closes the picker."
  [{:keys [store action]}]
  (let [[_ style] action
        clip (query/source-clip @store)]
    (swap! store
           (fn [state]
             (-> state
                 (common/transact (captions-tx clip style))
                 (update-in [:editor2 :captions-sidebar] dissoc :style-picker?))))))

(defn seconds->ms
  [value]
  (let [seconds (js/parseFloat value)]
    (when (js/isFinite seconds)
      (js/Math.round (* 1000 seconds)))))

(defn valid-times?
  "Within the media and with a length, since the caption compositions
   drop a word without one."
  [word media-duration]
  (and (< -1 (:word/start word) (:word/end word))
       (or (nil? media-duration)
           (<= (:word/end word) media-duration))))

(defn set-word!
  "Edits the start or the end of a word, entered in seconds; a value
   that would leave the media or the word without a length is
   ignored."
  [{:keys [store action]}]
  (let [[_ word-uuid attribute value] action
        word (ds/entity (get-in @store [:editor2 :db]) [:word/uuid word-uuid])
        ms (seconds->ms value)]
    (when (and word
               ms
               (contains? #{:word/start :word/end} attribute)
               (valid-times? (assoc (select-keys word [:word/start :word/end])
                                    attribute
                                    ms)
                             (get-in (query/source-clip @store) [:clip/asset :asset/duration])))
      (swap! store
             common/transact
             [{:word/uuid word-uuid
               attribute ms}]))))

(defn toggle-emphasis!
  "The emphasis is an edit of the transcript editor, so it takes part
   in its undo history."
  [{:keys [replicant/js-event]}]
  (editor/toggle-emphasis! (or (some-> ^js js-event .-target .-ownerDocument)
                               js/document)))

(defn timed-word?
  [word]
  (and (number? (:word/start word))
       (number? (:word/end word))))

(defn edit-words!
  "Stores the words of the transcript editor (`ui.captions.editor`):
   the difference to the stored words of the clip. An edit with a word
   without times is refused as a whole and reported, instead of
   failing inside the transaction (seen on production 2026-09-23:
   `Cannot store nil as a value`), where a half-applied edit made the
   editor and the store disagree and the next edit dropped words."
  [{:keys [store action]}]
  (let [[_ clip-uuid edited] action
        clip (ds/entity (get-in @store [:editor2 :db]) [:clip/uuid clip-uuid])
        untimed (remove timed-word? edited)]
    (if (seq untimed)
      (js/console.error "Transcript edit refused, words without times:"
                        (pr-str (mapv #(select-keys % [:word/uuid :word/text :word/start :word/end :word/break? :word/fresh?])
                                      untimed)))
      (let [tx (when clip
                 (transcript-edit/tx (get-in clip [:clip/asset :asset/uuid])
                                     (query/clip-stored-words clip)
                                     edited))]
        (when (seq tx)
          (swap! store common/transact tx))))))

(defn seek-word!
  "Moves the playhead to the start of the word, whose time is relative
   to the media."
  [{:keys [store action]}]
  (let [[_ word-uuid] action
        state @store
        clip (query/source-clip state)
        word (ds/entity (get-in state [:editor2 :db]) [:word/uuid word-uuid])]
    (when (and clip word)
      (swap! store
             assoc-in [:editor2 :playhead-position]
             (max (:clip/start clip)
                  (+ (:clip/start clip)
                     (- (:word/start word)
                        (or (:clip/offset clip) 0)))))
      (actions/seek! @store))))

(defn handler
  [kind f]
  {:ui.action/kind kind
   :ui.action/handler f})

(def handlers
  [(handler :captions/transcribe transcribe!)
   (handler :captions/transcribed transcribed!)
   (handler :captions/add add-captions!)
   (handler :captions/remove remove-captions!)
   (handler :captions/failed failed!)
   (handler :captions/set-source set-source!)
   (handler :captions/set-style set-style!)
   (handler :captions/set-word set-word!)
   (handler :captions/toggle-emphasis toggle-emphasis!)
   (handler :captions/edit-words edit-words!)
   (handler :captions/seek-word seek-word!)])
